<script lang="ts" setup>
import {ref} from 'vue';
import {useI18n} from 'vue-i18n';
import {useRouter} from 'vue-router';
import {useQuasar} from 'quasar';

import MultiViewLayout from '@/components/layouts/MultiViewLayout.vue';
import TheAbandonModal from '@/components/TheAbandonModal.vue';
import TheContactModal from '@/components/TheContactModal.vue';


import {updateCanaryMeetComputerRequirements} from '@/api/endpoints/patientWeb';
import {getNextActivationRoute} from '@/services/piqSetupService.ts';
import {handleApiRequest} from '@/api/handleApiRequest.ts';
import {computerReqsLogo} from '@/assets/svgCanary.ts';
import Svg from '@/components/Svg.vue';

const {t} = useI18n();
const router = useRouter();
const $q = useQuasar();


const checked = ref(false);

async function onContinueClicked() {
  // debugger;
  if (checked.value) {
    $q.loading.show();
    await handleApiRequest(() => updateCanaryMeetComputerRequirements(), null, $q)
      .then(async () => {
        const nextRoute = await getNextActivationRoute($q);
        router.push({name: nextRoute}).catch(() => {
        });
      }).finally(() => {
        $q.loading.hide();
      })
  }
}


const showAbandon = ref(false);

async function onAbandonClick() {
  showAbandon.value = !showAbandon.value;
}

const showContact = ref(false);

async function onInfoClick() {
  showContact.value = !showContact.value;
}

</script>

<template>
  <q-page padding class="canary setup requirements">
    <MultiViewLayout :onBackClick="onAbandonClick" back-icon-list="fa-solid fa-house-circle-xmark"
                     :box-padding="$zb.enums.BoxPaddingEnum.p303048">
      <!-- Title -->
      <template v-slot:title>
        {{ t('hbs_flow_title') }}
      </template>

      <!-- Heading -->
      <template v-slot:heading>
        {{ t('hbs_computer_requirements') }}
      </template>
      <template v-slot:auxiliary>
        <zbm-btn-icon @click="onInfoClick()" icon="fa-regular fa-question-circle" size="24px" text-color="dark-teal"/>
      </template>
      <!-- Main Content -->
      <template v-slot:default>
        <div class="row justify-center mb-20">
          <div class="col-auto" id="reqsIcon">
            <Svg :view-box="computerReqsLogo.viewBox" :vector-paths="computerReqsLogo.vectorPaths"/>
          </div>
        </div>

        <div class="row document has-checkbox">
          <div class="col">
            <p class="text-content-txt-secondary font-size-14 text-center mb-20">{{ t('hbs_computer_requirements_body') }}</p>
            <p>{{ t('hbs_your_computer_must') }}</p>
            <ol>
              <li>{{ t('hbs_computer_requirements_bullet_1') }}</li>
              <li>{{ t('hbs_computer_requirements_bullet_2') }}
                <ol type="a">
                  <li>{{ t('hbs_computer_requirements_bullet_2a') }}</li>
                  <li>{{ t('hbs_computer_requirements_bullet_2b') }}</li>
                </ol>
              </li>

              <li>{{ t('hbs_computer_requirements_bullet_3') }}</li>
              <!--              <li>{{ t('hbs_computer_requirements_bullet_4') }}</li>-->
            </ol>
          </div>
        </div>

        <div class="row justify-center q-gutter-lg">

          <div class="col-auto usb_wrapper typeC text-center">
            <img src="img/baseStation/cautionEmoji.png" class="emoji" alt=""/>
            <div class="column">
              <div class="col">
                <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="#FCAF17">
                  <path
                      d="M10.625 15C9.13316 15 7.70242 15.5268 6.64752 16.4645C5.59263 17.4021 5 18.6739 5 20C5 21.3261 5.59263 22.5979 6.64752 23.5355C7.70242 24.4732 9.13316 25 10.625 25H29.375C30.8668 25 32.2976 24.4732 33.3525 23.5355C34.4074 22.5979 35 21.3261 35 20C35 18.6739 34.4074 17.4021 33.3525 16.4645C32.2976 15.5268 30.8668 15 29.375 15H10.625ZM11.5625 19.1667H28.4375C28.6861 19.1667 28.9246 19.2545 29.1004 19.4107C29.2762 19.567 29.375 19.779 29.375 20C29.375 20.221 29.2762 20.433 29.1004 20.5893C28.9246 20.7455 28.6861 20.8333 28.4375 20.8333H11.5625C11.3139 20.8333 11.0754 20.7455 10.8996 20.5893C10.7238 20.433 10.625 20.221 10.625 20C10.625 19.779 10.7238 19.567 10.8996 19.4107C11.0754 19.2545 11.3139 19.1667 11.5625 19.1667Z"
                      fill="#FCAF17"/>
                </svg>
              </div>
              <div class="col">
                <p>{{ t('hbs_computer_requirements_usbc') }}</p>
              </div>
              <div class="col">
                <p class="font-size-12 text-body">{{ t('hbs_computer_requirements_usbc_description') }}</p>
              </div>
            </div>
          </div>

          <div class="col-auto usb_wrapper typeA text-center">
            <img src="img/baseStation/successEmoji.png" class="emoji" alt=""/>
            <div class="column">
              <div class="col">
                <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
                  <path
                      d="M1.25 12.5C0.918479 12.5 0.600537 12.6317 0.366117 12.8661C0.131696 13.1005 0 13.4185 0 13.75L0 26.25C0 26.5815 0.131696 26.8995 0.366117 27.1339C0.600537 27.3683 0.918479 27.5 1.25 27.5H38.75C39.0815 27.5 39.3995 27.3683 39.6339 27.1339C39.8683 26.8995 40 26.5815 40 26.25V13.75C40 13.4185 39.8683 13.1005 39.6339 12.8661C39.3995 12.6317 39.0815 12.5 38.75 12.5H1.25ZM5.625 16.25H34.375C34.5408 16.25 34.6997 16.3158 34.8169 16.4331C34.9342 16.5503 35 16.7092 35 16.875V19.375C35 19.5408 34.9342 19.6997 34.8169 19.8169C34.6997 19.9342 34.5408 20 34.375 20H5.625C5.45924 20 5.30027 19.9342 5.18306 19.8169C5.06585 19.6997 5 19.5408 5 19.375V16.875C5 16.7092 5.06585 16.5503 5.18306 16.4331C5.30027 16.3158 5.45924 16.25 5.625 16.25Z"
                      fill="#00A79D"/>
                </svg>
              </div>
              <div class="col">
                <p>{{ t('hbs_computer_requirements_usba') }}</p>
              </div>
              <div class="col">
                <p class="font-size-12 text-body">{{ t('hbs_computer_requirements_usba_description') }}</p>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col q-py-md">
            <!-- Checkbox -->
            <zbm-checkbox v-model="checked" @change="checked = !checked"
                          :label="t('hbs_computer_requirements_button_accept')" class="mt-48"/>
          </div>
        </div>
        <div class="row ">
          <zbm-btn :color="$zb.enums.ButtonStdColorEnum.accent" :label="t('acc_gen_continue')" :outline="true"
                   :width="$zb.enums.ButtonWidthEnum.wFull" :height="$zb.enums.ButtonHeightEnum.lg" :disabled="!checked"
                   @click="onContinueClicked"/>

          <zbm-btn :color="$zb.enums.ButtonStdColorEnum.whiteText"
                   :label="t('hbs_computer_requirements_button_need_help')" :outline="true"
                   :width="$zb.enums.ButtonWidthEnum.wFull" :height="$zb.enums.ButtonHeightEnum.lg"
                   @click="onAbandonClick" class="q-mt-md"/>
        </div>

      </template>
    </MultiViewLayout>
    <the-abandon-modal v-model="showAbandon"/>

    <the-contact-modal v-model="showContact"/>
  </q-page>
</template>

